/* ─────────────────────────────────────────────────────────────────────────────
   STAR WHAT-IF — its own tab, deliberately.

   StarPredictor answers "where do I stand", from published and derived facts.
   This answers "where would I stand if". They are different modes, and the
   scope document's hard rule is that a projected star must never be mistakable
   for a published one.

   Keeping the surfaces apart makes that structural rather than a styling
   convention somebody can forget — the same argument as the chart check's
   `Not checked` state, which exists so that "we could not look" can never be
   read as "we looked and found nothing".

   NOTHING IN StarPredictor IS MODIFIED OR REUSED BY THIS FILE. It is a
   separate component reading the same engine. If this tab were deleted
   tomorrow, every existing screen would behave exactly as it does today.

   ── THE TWO NUMBERS THIS SCREEN REFUSES TO SHOW ───────────────────────────

   1. A confident star when the engine returned a floor. The API returns `star`
      only when the score is exact and `star_at_best` when inputs were missing.
      This file renders them differently and never falls back from one to the
      other, because our blind spots (substandard quality of care, revisit
      counts) can only make the score too LOW — and a lower score is a BETTER
      star. The error runs in the flattering direction, which is the one a
      facility acts on.

   2. A projection presented beside the published rating without a boundary.
      The two live in separate panels with separate headings.
   ───────────────────────────────────────────────────────────────────────── */

import React from "react";

const OPS = [
  { op: "vacate",       label: "Vacate a citation",            needs: ["id"] },
  { op: "demote",       label: "Demote a citation",            needs: ["id", "to"] },
  { op: "add",          label: "Add a deficiency",             needs: ["tag", "severity"] },
  { op: "new_survey",   label: "Simulate an annual survey",    needs: ["citations"] },
  { op: "clean_survey", label: "Simulate a clean survey",      needs: [] },
];

const SEVERITIES = "ABCDEFGHIJKL".split("");

const STAR_COLOR = (n) =>
  n >= 4 ? "var(--ed-success)" : n === 3 ? "#d97706" : n === 2 ? "#ea580c" : "var(--ed-alert)";

function Stars({ n, size = 26, dim = false }) {
  const v = Number(n) || 0;
  if (!v) return <span style={{ color: "var(--ed-line-hover)", fontSize: size }}>—</span>;
  return (
    <span style={{ fontSize: size, letterSpacing: 2, fontWeight: 700,
                   color: dim ? "var(--ed-muted-2)" : STAR_COLOR(v) }}>
      {"★".repeat(v)}{"☆".repeat(5 - v)}
    </span>
  );
}

/* ── THE STAR ON THIS SCREEN IS ONE DOMAIN OF FOUR ─────────────────────────
   Predictive Analytics shows Overall, Health Inspection, Staffing and Quality
   Measures across the top, and this panel showed a bare 1★ → 2★ underneath.
   There is only one way a reader takes that, and it is the wrong one.

   Measured on ARC at Trotwood (CCN 365309): health 1, staffing 1, QM 4,
   published overall 1. The clean-survey scenario takes the health inspection
   star to 2 and leaves the OVERALL rating at 1, because CMS's step 2 subtracts
   a star for one-star staffing — the survey earns a star and staffing cancels
   it. Someone reading "1 → 2" took a number to a board meeting that Care
   Compare will never display.

   THE PROJECTION COMES FROM THE SERVER, not from arithmetic here. Three domain
   stars, two caps and a ceiling rule are not a thing to implement twice; #289
   recorded why a second arithmetic in the browser is a second thing that can
   disagree with the score. Guarded for absence throughout, because the two
   repositories deploy independently and a browser can be running ahead of the
   API answering it — without `overall` the panel renders exactly as it did. */
function OverallProjection({ overall }) {
  if (!overall) return null;
  const { domains, from_published: pub, projected, self_check: check, domain_note: note } = overall;

  const Row = ({ k, v }) => (
    <div style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 2 }}>
      <span style={{ fontSize: 12, color: "var(--ed-muted-2)", minWidth: 128 }}>{k}</span>
      <span style={{ fontSize: 13, color: "var(--ed-ink-2)" }}>{v}</span>
    </div>
  );

  // A star, a range, or the reason there is neither. Never a bare dash: "we
  // could not work it out" and "it does not move" are different facts.
  const show = (o) => {
    if (!o) return <span style={{ fontSize: 13, color: "var(--ed-muted-2)" }}>—</span>;
    if (o.star != null) return <Stars n={o.star} size={20} />;
    if (o.range) {
      return (
        <span style={{ fontSize: 13, color: "#92400e" }}>
          somewhere between <strong>{o.range.min}</strong> and <strong>{o.range.max}</strong> stars
        </span>
      );
    }
    return <span style={{ fontSize: 13, color: "#92400e" }}>no rating</span>;
  };

  const movedOverall = projected && pub && projected.star != null && pub.star != null
    ? projected.star !== pub.star
    : null;

  return (
    <div style={{ marginTop: 18, padding: 13, background: "var(--ed-bg)",
                  border: "1px solid var(--ed-line)", borderRadius: 8 }}>
      <Label>What this does to the overall rating</Label>

      {/* THE LINE ITSELF, first and unconditional. */}
      <div style={{ fontSize: 13, color: "var(--ed-ink-2)", lineHeight: 1.55, margin: "4px 0 10px" }}>
        {note}
      </div>

      <div style={{ display: "flex", gap: 30, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                        letterSpacing: ".09em", color: "var(--ed-muted-2)", marginBottom: 4 }}>
            Overall now
          </div>
          {show(pub)}
        </div>
        <div style={{ alignSelf: "center", fontSize: 20, color: "var(--ed-muted-2)" }}>→</div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                        letterSpacing: ".09em", color: "var(--ed-muted-2)", marginBottom: 4 }}>
            Overall under this scenario
          </div>
          {show(projected)}
          {movedOverall === false && (
            <div style={{ fontSize: 12.5, color: "#92400e", marginTop: 4, maxWidth: 360, fontWeight: 600 }}>
              Unchanged. The health inspection star moves; the overall rating does not.
            </div>
          )}
        </div>
      </div>

      {/* CMS's own three steps, from the payload, so "why not" has an answer
          here rather than in the Technical Users' Guide. */}
      {Array.isArray(projected?.steps) && projected.steps.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {projected.steps.map((s) => (
            <Row
              key={s.step}
              k={`Step ${s.step} · ${s.label}`}
              v={
                <>
                  {s.rating != null && <>{s.rating}★ → </>}
                  <strong>{s.result}★</strong>
                  {s.note ? <span style={{ color: "var(--ed-muted-2)" }}> — {s.note}</span> : null}
                </>
              }
            />
          ))}
        </div>
      )}

      {/* A cap is the whole answer wherever it applies, so it is not a footnote. */}
      {Array.isArray(projected?.caps) && projected.caps.length > 0 && (
        <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 12.5, color: "#92400e", lineHeight: 1.5 }}>
          {projected.caps.map((c, i) => <li key={i}>{c.note}</li>)}
        </ul>
      )}

      {projected?.why && (
        <div style={{ fontSize: 12.5, color: "#92400e", marginTop: 8, lineHeight: 1.5 }}>{projected.why}</div>
      )}

      {/* THE DOMAIN STARS THIS WAS BUILT FROM, because a reader checking the
          projection against Care Compare needs to see the inputs. */}
      <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid var(--ed-line)" }}>
        <Row k="Staffing" v={domains?.staffing != null ? `${domains.staffing}★ (published)` : "not held"} />
        <Row k="Quality measures" v={domains?.quality_measures != null ? `${domains.quality_measures}★ (published)` : "not held"} />
      </div>

      {/* WHERE THE RULE DOES NOT REBUILD WHAT CMS PUBLISHED, say so loudly.
          The three domain stars and the published overall come from one CMS row,
          so a mismatch means either a cached row mixing two monthly releases or
          a wrong rule — and a projection on top of either is not worth reading.
          A passing check is not printed: a screen that congratulates itself on
          every load trains people to skip the line that matters. */}
      {check?.checked && check.agrees === false && (
        <div style={{ marginTop: 10, padding: 9, background: "var(--ed-alert-soft)",
                      border: "1px solid var(--ed-alert-line)", borderRadius: 7,
                      fontSize: 12.5, color: "#b91c1c", lineHeight: 1.5 }}>
          <strong>Do not rely on the projection above.</strong> {check.note} Published overall{" "}
          {check.published}★; rebuilt from the published domain stars {check.computed}★.
        </div>
      )}
    </div>
  );
}

/** The qualifier is part of the number. A star with a floor behind it is
 *  rendered as a ceiling and says so in words, not in a tooltip. */
function RatedStar({ rating, label }) {
  const exact = rating?.star != null;
  const best = rating?.star_at_best != null;
  if (!exact && !best) {
    return (
      <div>
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                      letterSpacing: ".09em", color: "var(--ed-muted-2)", marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 14, color: "var(--ed-muted-2)" }}>
          {rating?.reason || "Not available"}
        </div>
      </div>
    );
  }
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                    letterSpacing: ".09em", color: "var(--ed-muted-2)", marginBottom: 4 }}>
        {label}
      </div>
      <Stars n={exact ? rating.star : rating.star_at_best} dim={!exact} />
      {!exact && (
        <div style={{ fontSize: 12, color: "#92400e", marginTop: 4, maxWidth: 320 }}>
          At best. Inputs we cannot see can only add points, and more points is
          a worse star — so the real rating is this or lower.
        </div>
      )}
    </div>
  );
}

export default function StarWhatIf({ ccn, facilityName, engineBase, authHeaders }) {
  const [baseline, setBaseline] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);

  const [changes, setChanges] = React.useState([]);
  const [draft, setDraft] = React.useState({ op: "vacate", id: "", to: "D", tag: "", severity: "D" });
  const [result, setResult] = React.useState(null);
  const [running, setRunning] = React.useState(false);
  const [runError, setRunError] = React.useState(null);
  // Refusing a duplicate silently would read as a broken button.
  const [dupNotice, setDupNotice] = React.useState(null);

  const url = React.useCallback(
    (suffix) => `${engineBase}/facilities/${encodeURIComponent(ccn)}/five-star${suffix || ""}`,
    [engineBase, ccn]
  );

  React.useEffect(() => {
    let live = true;
    if (!ccn) { setLoading(false); return undefined; }
    setLoading(true); setLoadError(null);
    // survaiEngineHeaders() is async — it reads the session token. Awaiting it
    // rather than spreading a Promise is the difference between an
    // authenticated request and a 401 that looks like an empty facility.
    (async () => (await (authHeaders ? authHeaders() : {})))()
      .then((hdrs) => fetch(url(), { headers: hdrs }))
      .then(async (r) => {
        const body = await r.json().catch(() => null);
        if (!r.ok) throw new Error(body?.error?.message || `HTTP ${r.status}`);
        return body?.data ?? body;
      })
      .then((d) => { if (live) { setBaseline(d); setLoading(false); } })
      // A failed read is reported, never rendered as an empty facility. The two
      // are different facts and only one of them is about the building.
      .catch((e) => { if (live) { setLoadError(e.message); setLoading(false); } });
    return () => { live = false; };
  }, [ccn, url, authHeaders]);

  const citations = baseline?.citations_read ?? 0;
  const derived = baseline?.derived;

  function addChange() {
    const spec = OPS.find((o) => o.op === draft.op);
    if (!spec) return;
    let ch = { op: draft.op };
    if (draft.op === "vacate") { if (!draft.id) return; ch.id = draft.id; }
    if (draft.op === "demote") { if (!draft.id) return; ch.id = draft.id; ch.to = draft.to; }
    if (draft.op === "add") {
      if (!/^F\d+$/i.test(draft.tag.trim())) return;
      ch.tag = draft.tag.trim().toUpperCase(); ch.severity = draft.severity;
    }
    if (draft.op === "new_survey") {
      if (!/^F\d+$/i.test(draft.tag.trim())) return;
      ch.citations = [{ tag: draft.tag.trim().toUpperCase(), severity: draft.severity }];
    }
    // VACATING THE SAME CITATION TWICE IS NOT A SCENARIO, IT IS A MIS-CLICK.
    // The list happily took the duplicate and the header counted it, so a
    // scenario could read "3 changes" while describing two — and the second
    // vacate is a no-op on the server anyway, since the row is already gone.
    // Refused for vacate and demote, which name an existing citation by id;
    // `add` and `new_survey` are NOT deduped, because adding the same
    // hypothetical finding twice is a legitimate thing to model.
    if (ch.op === "vacate" || ch.op === "demote") {
      const already = changes.some((c) => c.op === ch.op && c.id === ch.id);
      if (already) {
        setDupNotice(`That citation is already in the scenario — ${ch.op === "vacate" ? "vacated" : "demoted"}.`);
        return;
      }
    }
    setDupNotice(null);
    setChanges((c) => [...c, ch]);
    setResult(null);
  }

  function run() {
    setRunning(true); setRunError(null);
    (async () => (await (authHeaders ? authHeaders() : {})))()
      .then((hdrs) => fetch(url("/whatif"), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...hdrs },
        body: JSON.stringify({ changes }),
      }))
      .then(async (r) => {
        const body = await r.json().catch(() => null);
        if (!r.ok) throw new Error(body?.error?.message || `HTTP ${r.status}`);
        return body?.data ?? body;
      })
      .then((d) => { setResult(d); setRunning(false); })
      .catch((e) => { setRunError(e.message); setRunning(false); });
  }

  if (!ccn) {
    return <Notice>Add a CMS CCN to this facility to model star scenarios.</Notice>;
  }
  if (loading) return <Notice>Reading this facility’s citation history…</Notice>;
  if (loadError) {
    return (
      <Notice tone="error">
        Could not read the five-star baseline: {loadError}
        <div style={{ marginTop: 6, fontSize: 13 }}>
          This is a failure to look, not a finding about the facility.
        </div>
      </Notice>
    );
  }

  return (
    <div data-hint="pa-star-whatif">
      {/* ── where the facility stands, kept apart from any projection ─────── */}
      <Panel>
        <Head>Today, before any scenario</Head>
        <div style={{ display: "flex", gap: 32, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <Label>Published by CMS</Label>
            <Stars n={baseline?.published?.health_inspection_rating} />
            <div style={{ fontSize: 12, color: "var(--ed-muted-2)", marginTop: 4 }}>
              Health inspection rating, from Care Compare
            </div>
          </div>
          <RatedStar rating={derived} label="Derived from the citations we hold" />
          <div>
            <Label>Weighted score</Label>
            <div style={{ fontSize: 26, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
              {derived?.score ?? "—"}
            </div>
            <div style={{ fontSize: 12, color: "var(--ed-muted-2)", marginTop: 4 }}>
              Lower is better. {citations} citation{citations === 1 ? "" : "s"} read.
            </div>
          </div>
        </div>

        {derived?.headroom?.to_star && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--ed-line)" }}>
            <strong>{derived.headroom.points_to_shed}</strong> points to reach{" "}
            {derived.headroom.to_star} stars in {baseline.state} — the cut point is{" "}
            {derived.headroom.threshold}.
            {derived.headroom.note && (
              <span style={{ color: "#92400e" }}> {derived.headroom.note}</span>
            )}
          </div>
        )}
        {derived?.slack && (
          <div style={{ marginTop: 6, fontSize: 13, color: "var(--ed-muted-2)" }}>
            {derived.slack.points_of_room} points of room before falling to{" "}
            {derived.slack.falls_to_star} stars (above {derived.slack.at_score_above}).
          </div>
        )}
        {derived?.reason && (
          <div style={{ marginTop: 12, fontSize: 13, color: "#92400e" }}>{derived.reason}</div>
        )}
      </Panel>

      {/* ── what we could not see. Stated, never implied by absence. ──────── */}
      {baseline?.completeness?.understates?.length > 0 && (
        <Panel tone="warn">
          <Head>What this score cannot see</Head>
          <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
            {baseline.completeness.understates.map((u, i) => (
              <li key={`u${i}`} style={{ marginBottom: 6, fontSize: 14 }}>
                <strong>{u.input}</strong> — {u.effect}
              </li>
            ))}
            {baseline.completeness.overstates?.map((o, i) => (
              <li key={`o${i}`} style={{ marginBottom: 6, fontSize: 14 }}>
                <strong>{o.input}</strong> — {o.effect}
              </li>
            ))}
          </ul>
          <div style={{ marginTop: 10, fontSize: 13 }}>{baseline.completeness.summary}</div>
        </Panel>
      )}

      {/* ── build a scenario ─────────────────────────────────────────────── */}
      <Panel>
        <Head>Model a change</Head>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginTop: 10 }}>
          <Field label="Operation">
            <select value={draft.op} onChange={(e) => setDraft({ ...draft, op: e.target.value })} style={inputStyle}>
              {OPS.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
            </select>
          </Field>

          {(draft.op === "vacate" || draft.op === "demote") && (
            <Field label="Citation">
              <select value={draft.id} onChange={(e) => setDraft({ ...draft, id: e.target.value })} style={{ ...inputStyle, minWidth: 260 }}>
                <option value="">Choose a citation…</option>
                {(baseline?.derived && baseline.citation_list ? baseline.citation_list : []).map((c) => (
                  <option key={c.id} value={c.id}>{c.tag_short} · {c.scope_severity_code} · {c.survey_date}</option>
                ))}
              </select>
            </Field>
          )}
          {draft.op === "demote" && (
            <Field label="To severity">
              <select value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} style={inputStyle}>
                {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          )}
          {(draft.op === "add" || draft.op === "new_survey") && (
            <>
              <Field label="F-tag">
                <input value={draft.tag} placeholder="F689"
                       onChange={(e) => setDraft({ ...draft, tag: e.target.value })}
                       style={{ ...inputStyle, width: 110 }} />
              </Field>
              <Field label="Severity">
                <select value={draft.severity} onChange={(e) => setDraft({ ...draft, severity: e.target.value })} style={inputStyle}>
                  {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </>
          )}

          <button onClick={addChange} style={btnStyle}>Add to scenario</button>
        </div>

        {dupNotice && (
          <div style={{ marginTop: 8, fontSize: 13, color: "var(--ed-alert)" }}>{dupNotice}</div>
        )}

        {changes.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Label>Scenario — {changes.length} change{changes.length === 1 ? "" : "s"}</Label>
            <ol style={{ margin: "6px 0 0", paddingLeft: 20 }}>
              {changes.map((c, i) => (
                <li key={i} style={{ fontSize: 14, marginBottom: 4 }}>
                  {describe(c)}
                  <button onClick={() => { setChanges(changes.filter((_, j) => j !== i)); setResult(null); setDupNotice(null); }}
                          style={linkBtn}>remove</button>
                </li>
              ))}
            </ol>
            <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
              <button onClick={run} disabled={running} style={{ ...btnStyle, opacity: running ? 0.6 : 1 }}>
                {running ? "Scoring…" : "Score this scenario"}
              </button>
              <button onClick={() => { setChanges([]); setResult(null); setDupNotice(null); }} style={{ ...btnStyle, background: "transparent", color: "var(--ed-ink-3)", border: "1px solid var(--ed-line)" }}>
                Clear
              </button>
            </div>
          </div>
        )}
        {runError && <div style={{ marginTop: 10, color: "var(--ed-alert)", fontSize: 14 }}>{runError}</div>}
      </Panel>

      {/* ── the projection, boxed and labelled ───────────────────────────── */}
      {result && (
        <Panel tone="projected">
          <Head>
            Projected — not a CMS rating
          </Head>

          {/* ── WHAT WAS SCORED IS NOT ALWAYS WHAT WAS ASKED FOR ──────────────
              This block used to sit BELOW the stars, so a three-change scenario
              where two changes failed rendered a confident 1★ → 2★ with the
              failure as a footnote underneath. The numbers described a
              different scenario from the one listed above them, and nothing on
              screen said so. It goes first now, and the numbers are visibly
              demoted while it is showing — a projection for the wrong input is
              not a weaker answer, it is an answer to a question nobody asked. */}
          {result.changes_rejected?.length > 0 && (
            <div style={{ marginBottom: 14, padding: 12, background: "#fff1f2",
                          border: "1px solid #fecdd3", borderRadius: 8 }}>
              <strong style={{ fontSize: 14 }}>
                {result.changes_rejected.length} of {(result.changes_applied?.length || 0) + result.changes_rejected.length}{" "}
                change{result.changes_rejected.length === 1 ? "" : "s"} did not apply — the figures below are for the
                {" "}{result.changes_applied?.length || 0} that did, not for the scenario you built.
              </strong>
              <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                {result.changes_rejected.map((r, i) => (
                  <li key={i} style={{ fontSize: 13 }}>{r.why}</li>
                ))}
              </ul>
              <div style={{ fontSize: 12, marginTop: 8, color: "#9f1239" }}>
                A citation id goes stale when CMS data for this facility is refreshed. Reload the
                page to pick it again.
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 32, flexWrap: "wrap", marginTop: 10,
                        opacity: result.all_changes_applied === false ? 0.55 : 1 }}>
            <RatedStar rating={result.before?.rating} label="Now" />
            <div style={{ alignSelf: "center", fontSize: 24, color: "var(--ed-muted-2)" }}>→</div>
            <RatedStar
              rating={result.after?.rating}
              label={result.all_changes_applied === false ? "Under the changes that applied" : "Under this scenario"}
            />
            <div>
              {/* FEWER POINTS IS A BETTER STAR, so a bare "−15" reads backwards
                  to anyone who has not internalised that. The number keeps its
                  sign — it is a real arithmetic delta — and the words say which
                  direction it is. */}
              <Label>Score change</Label>
              <div style={{ fontSize: 24, fontWeight: 800, fontVariantNumeric: "tabular-nums",
                            color: result.delta?.score < 0 ? "var(--ed-success)" : result.delta?.score > 0 ? "var(--ed-alert)" : "var(--ed-ink-3)" }}>
                {result.delta?.score > 0 ? "+" : ""}{result.delta?.score ?? "—"}
              </div>
              {/* "POINTS REMOVED" READ AS "CITATIONS REMOVED", which is the one
                  reading that makes a new-survey scenario look like a bug: a
                  citation is ADDED and this line said points were removed. It
                  now describes the score and the star, which is all this number
                  is about — what moved the score is the table below. */}
              <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2,
                            color: result.delta?.score < 0 ? "var(--ed-success)" : result.delta?.score > 0 ? "var(--ed-alert)" : "var(--ed-ink-3)" }}>
                {result.delta?.score < 0 ? "lower score — better rating"
                  : result.delta?.score > 0 ? "higher score — worse rating"
                  : "no change"}
              </div>
              <div style={{ fontSize: 12, color: "var(--ed-muted-2)", marginTop: 4 }}>
                {result.before?.score ?? "—"} → {result.after?.score ?? "—"} points (lower is better)
              </div>
            </div>
          </div>

          {/* THE OVERALL RATING, IMMEDIATELY UNDER THE STARS THAT GET MISREAD.
              Not at the bottom: the misreading happens the moment someone sees
              1★ → 2★, so the correction has to be in the same glance. The
              post-incident audit learned this the expensive way — honest prose
              placed below a wrong figure does not get read as a qualifier. */}
          <OverallProjection overall={result.overall} />

          {/* ── WHERE THE DELTA CAME FROM ──────────────────────────────────
              Without this, simulating an annual survey reads as a bug: a
              citation is ADDED and the score falls. Measured on CCN 365309 with
              one F689/D, the standard cycles went 62 → 15 while the complaint
              surveys never moved — the whole −47 was one 104-point cycle ageing
              out of the two that are rated. The total alone cannot show that.

              The parts come from the server and sum to the headline, so this is
              a rendering of the arithmetic rather than a second opinion about
              it. Guarded for absence: a browser can be running ahead of the API
              that answers it, and the panel is still correct without the table. */}
          {Array.isArray(result.moved) && result.moved.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <Label>Where the change came from</Label>
              <table style={{ borderCollapse: "collapse", marginTop: 6, fontSize: 13,
                              fontVariantNumeric: "tabular-nums" }}>
                <thead>
                  <tr style={{ color: "var(--ed-muted-2)", textAlign: "left" }}>
                    <th style={{ padding: "4px 18px 4px 0", fontWeight: 600 }}>Part of the score</th>
                    <th style={{ padding: "4px 18px 4px 0", fontWeight: 600, textAlign: "right" }}>Now</th>
                    <th style={{ padding: "4px 18px 4px 0", fontWeight: 600, textAlign: "right" }}>Scenario</th>
                    <th style={{ padding: "4px 0", fontWeight: 600, textAlign: "right" }}>Change</th>
                  </tr>
                </thead>
                <tbody>
                  {result.moved.map((p, i) => (
                    <tr key={i} style={{ borderTop: "1px solid var(--ed-line)" }}>
                      <td style={{ padding: "6px 18px 6px 0" }}>{p.part}</td>
                      <td style={{ padding: "6px 18px 6px 0", textAlign: "right" }}>{p.before}</td>
                      <td style={{ padding: "6px 18px 6px 0", textAlign: "right" }}>{p.after}</td>
                      <td style={{ padding: "6px 0", textAlign: "right", fontWeight: 700,
                                   color: p.delta < 0 ? "var(--ed-success)" : p.delta > 0 ? "var(--ed-alert)" : "var(--ed-muted-2)" }}>
                        {p.delta > 0 ? "+" : ""}{p.delta}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* THE SERVER ALREADY EXPLAINS THE SURPRISING OPERATIONS and the
              screen was throwing it away. `new_survey` returns "Existing cycles
              rolled back one; what was cycle 2 is no longer rated." — the one
              sentence that makes an added citation lowering the score make
              sense. Rendered from the payload rather than restated here, so the
              explanation cannot drift from the behaviour it describes. */}
          {result.changes_applied?.some((c) => c.note) && (
            <ul style={{ marginTop: 14, paddingLeft: 18, fontSize: 13,
                         color: "var(--ed-ink-3)", lineHeight: 1.5 }}>
              {result.changes_applied.filter((c) => c.note).map((c, i) => (
                <li key={i} style={{ marginBottom: 4 }}>{c.note}</li>
              ))}
            </ul>
          )}

          <div style={{ marginTop: 14, fontSize: 13, color: "#78350f", lineHeight: 1.5 }}>
            {result.disclaimer}
          </div>
        </Panel>
      )}
    </div>
  );
}

function describe(c) {
  if (c.op === "vacate") return `Vacate citation ${c.id}`;
  if (c.op === "demote") return `Demote citation ${c.id} to ${c.to}`;
  if (c.op === "add") return `Add ${c.tag} at ${c.severity}`;
  if (c.op === "new_survey") return `New annual survey: ${(c.citations || []).map((x) => `${x.tag}/${x.severity}`).join(", ")}`;
  if (c.op === "clean_survey") return "A clean annual survey";
  return JSON.stringify(c);
}

/* ── small presentational pieces, local to this file ────────────────────── */
const inputStyle = {
  padding: "8px 10px", border: "1px solid var(--ed-line)", borderRadius: 6,
  fontSize: 14, background: "var(--ed-canvas)", color: "var(--ed-ink-1)",
};
const btnStyle = {
  padding: "9px 16px", border: "none", borderRadius: 6, cursor: "pointer",
  background: "var(--ed-brand)", color: "#fff", fontSize: 14, fontWeight: 700,
};
const linkBtn = {
  marginLeft: 8, border: "none", background: "none", cursor: "pointer",
  color: "var(--ed-brand)", fontSize: 12, textDecoration: "underline", padding: 0,
};

function Panel({ children, tone }) {
  const bg = tone === "warn" ? "#fffbeb" : tone === "projected" ? "#fff7ed" : "var(--ed-canvas)";
  const border = tone === "warn" ? "#fde68a" : tone === "projected" ? "#fdba74" : "var(--ed-line)";
  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10,
                  padding: 20, marginBottom: 14 }}>
      {children}
    </div>
  );
}
function Head({ children }) {
  return <div style={{ fontSize: 16, fontWeight: 800, color: "var(--ed-ink-1)" }}>{children}</div>;
}
function Label({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                  letterSpacing: ".09em", color: "var(--ed-muted-2)", marginBottom: 4 }}>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return <div><Label>{label}</Label>{children}</div>;
}
function Notice({ children, tone }) {
  return (
    <div style={{ padding: 20, borderRadius: 10,
                  background: tone === "error" ? "#fff1f2" : "#fef3c7",
                  border: `1px solid ${tone === "error" ? "#fecdd3" : "#fcd34d"}`,
                  color: tone === "error" ? "#86163b" : "#78350f" }}>
      {children}
    </div>
  );
}
